import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import api, { formatMoney } from "@/lib/api";
import Logo from "@/components/Logo";
import { LogOut, ExternalLink } from "lucide-react";

export default function CustomerDashboard() {
    const { user, logout } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/me/quotes");
                setQuotes(data);
            } catch (err) { /* noop */ } finally { setLoading(false); }
        })();
    }, []);

    return (
        <div className="min-h-screen">
            <header className="border-b border-border bg-background/85 backdrop-blur-md sticky top-0 z-40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <Logo variant="seal" size={40} />
                        <span className="font-display text-2xl leading-none">Hacienda Nápoles</span>
                    </Link>
                    <div className="flex items-center gap-6">
                        <span className="overline text-muted-foreground hidden md:inline" data-testid="customer-email">{user?.email}</span>
                        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="rounded-none" data-testid="btn-customer-logout">
                            <LogOut className="h-4 w-4 mr-2" /> Salir
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1100px] mx-auto px-6 md:px-12 py-12">
                <div className="overline text-muted-foreground">Mi cuenta</div>
                <h1 className="font-display text-5xl md:text-6xl tracking-tighter mt-3">Tus cotizaciones.</h1>
                <p className="text-muted-foreground mt-3 max-w-xl">
                    Aquí encuentras todas las cotizaciones que has generado, su estado y los detalles de tu reserva si aplica.
                </p>

                <Separator className="my-8" />

                {loading ? (
                    <div className="overline text-muted-foreground">Cargando…</div>
                ) : quotes.length === 0 ? (
                    <div className="bg-white border border-border p-10 text-center" data-testid="empty-quotes">
                        <div className="font-display text-2xl mb-2">Aún no tienes cotizaciones.</div>
                        <p className="text-sm text-muted-foreground mb-6">Crea tu primera cotización y nuestro equipo te contactará para coordinar la visita.</p>
                        <Link to="/">
                            <Button className="rounded-none bg-foreground text-background hover:bg-foreground/85">
                                Crear cotización
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="quotes-list">
                        {quotes.map((q) => (
                            <article key={q.id} className="bg-white border border-border p-6 md:p-8" data-testid={`quote-${q.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="overline text-muted-foreground tabular">
                                            {(q.created_at || "").slice(0, 10)} · Lead #{(q.id || "").slice(0, 8)}
                                        </div>
                                        <h2 className="font-display text-3xl mt-1">
                                            {q.bride_name} <span className="text-muted-foreground">&</span> {q.groom_name}
                                        </h2>
                                    </div>
                                    <div className="text-right">
                                        <div className="overline text-muted-foreground">Total referencial</div>
                                        <div className="font-display text-3xl tabular mt-1">{formatMoney(q.total)}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-xs">
                                    <Meta label="Invitados" value={q.guests} />
                                    <Meta label="Temporada" value={q.season === "alta" ? "Alta" : "Baja"} />
                                    <Meta label="Fecha" value={q.date_mode === "exact" ? q.exact_date : (q.tentative_months || []).join(", ") || "—"} />
                                    <Meta label="Locación" value={(q.locations || (q.location ? [q.location] : [])).join(", ") || "—"} />
                                </div>

                                <div className="mt-6 flex flex-wrap gap-3">
                                    <a
                                        href={`https://calendly.com/aperez-ajcodelabs/30min`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 text-sm"
                                        data-testid={`btn-meeting-${q.id}`}
                                    >
                                        Agendar reunión <ExternalLink className="h-3 w-3" />
                                    </a>
                                    <a
                                        href={`https://wa.me/593999999999?text=${encodeURIComponent(`Hola, soy ${q.contact_name}, deseo seguimiento sobre mi cotización #${(q.id || "").slice(0, 8)} (boda ${q.bride_name} & ${q.groom_name}).`)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 border border-foreground px-4 py-2 text-sm hover:bg-foreground hover:text-background transition-colors"
                                    >
                                        Contactar por WhatsApp <ExternalLink className="h-3 w-3" />
                                    </a>
                                </div>

                                <div className="mt-6 pt-4 border-t border-border text-[11px] text-muted-foreground">
                                    <strong className="text-foreground">Estado de reserva:</strong> Pendiente — La fecha solo se bloquea con depósito y contrato firmado.
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div>
            <div className="overline text-muted-foreground">{label}</div>
            <div className="font-display text-lg mt-1 capitalize">{value || "—"}</div>
        </div>
    );
}
